import { useCallback } from "react";
import { BubbleMenu } from "@tiptap/react/menus";
import { Pencil, Trash2 } from "lucide-react";

// The embedded blocks you can select but not type into. Each one already has a
// dialog behind it; this bar is simply the way back into that dialog, because a
// video swallows clicks and nothing on the toolbar looked like "edit this one".
const BLOCKS = [
  { name: "kbEmbed", label: (a) => (a.kind === "pdf" ? "PDF" : "Video") },
  { name: "kbHtmlBlock", label: () => "Interactive block" },
  { name: "kbMermaid", label: () => "Diagram" },
  { name: "kbVideo", label: () => "Uploaded video" },
  { name: "kbAudio", label: () => "Audio" },
  { name: "kbAttachment", label: () => "Attachment" },
];
const EDITABLE = ["kbEmbed", "kbHtmlBlock", "kbMermaid"];
// Module-level so the props keep their identity — a fresh object on every
// render restarts the floating-ui effect and React bails out with
// "maximum update depth exceeded".
const OPTIONS = { placement: "top", offset: 8 };

/** A small bar over the selected block: edit it, or take it out. */
export function KbBlockToolbar({ editor, onEdit, enabled = true }) {
  const shouldShow = useCallback(
    ({ editor: ed }) => enabled && BLOCKS.some((b) => ed.isActive(b.name)), [enabled]);
  if (!editor) return null;
  const active = BLOCKS.find((b) => editor.isActive(b.name));
  const attrs = active ? editor.getAttributes(active.name) : {};

  return (
    <BubbleMenu editor={editor} shouldShow={shouldShow} options={OPTIONS} updateDelay={100}>
      <div data-testid="kb-block-toolbar"
        className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-1.5 py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900">
        <span className="px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          {active ? active.label(attrs) : ""}
        </span>
        {active && EDITABLE.includes(active.name) && (
          <button type="button" onClick={() => onEdit?.(active.name)}
            data-testid="kb-block-toolbar-edit"
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800">
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
        )}
        <button type="button" onClick={() => editor.chain().focus().deleteSelection().run()}
          data-testid="kb-block-toolbar-remove"
          className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-rose-600 transition-colors hover:bg-rose-50 dark:hover:bg-rose-950/50">
          <Trash2 className="h-3.5 w-3.5" /> Remove
        </button>
      </div>
    </BubbleMenu>
  );
}
